import { Spinner, VStack } from '@chakra-ui/react';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useEffect, useRef, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { v4 as uuidv4 } from 'uuid';

import { useAuth } from 'auth/AuthProvider';
import Text from 'components/Text';
import { REF_COOKIE_KEY, GITHUB_COOKIE_KEY } from 'config/constants/github';
import { AccountCreateParams, EdgeFunctionName } from 'types/edgeFunctions';
import { deleteCookie, getCookie } from 'utils/cookies';
import { logError } from 'utils/errorLogger';
import { getMetaTrackingData } from 'utils/metaTracking';

const IDEMPOTENCY_KEY = 'idempotency-key';
const MAX_TOKEN_WAIT_ATTEMPTS = 6;
const TOKEN_WAIT_DELAY_MS = 500;

const AccountCreatingStep = () => {
  const supabase = useSupabaseTypedClient();
  const { toastError } = useToastWrapper();
  const { signOut } = useAuth();
  const [isFinalizing, setIsFinalizing] = useState(false);
  const cancelledRef = useRef(false);
  const [idempotencyKey, _] = useLocalStorageState<string>(IDEMPOTENCY_KEY, {
    defaultValue: uuidv4(),
  });

  useEffect(() => {
    cancelledRef.current = false;

    const waitForGithubToken = async (): Promise<string> => {
      for (let i = 0; i < MAX_TOKEN_WAIT_ATTEMPTS; i++) {
        const token = getCookie(GITHUB_COOKIE_KEY);
        if (token) return token;
        if (cancelledRef.current) return '';
        await new Promise((r) => setTimeout(r, TOKEN_WAIT_DELAY_MS));
      }
      return '';
    };

    const finalizeOnboarding = async () => {
      if (isFinalizing) return;

      setIsFinalizing(true);

      try {
        const githubAccessToken = await waitForGithubToken();

        if (cancelledRef.current) return;

        if (!githubAccessToken) {
          logError('frontend', 'onboarding_error', 'GitHub token not found after wait', undefined, {
            url: window.location.href,
          });
          toastError('GitHub authentication expired. Please sign in again.', true);
          await signOut();
          return;
        }

        const refCode = getCookie(REF_COOKIE_KEY);
        const { fbc, fbp } = getMetaTrackingData();

        const { error } = await supabase.functions.invoke(EdgeFunctionName.AccountCreate, {
          body: {
            github_access_token: githubAccessToken,
            ref_code: refCode,
            fbc,
            fbp,
          } as AccountCreateParams,
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        });

        if (error) throw error;

        deleteCookie(GITHUB_COOKIE_KEY);
        deleteCookie(REF_COOKIE_KEY);
        localStorage.removeItem(IDEMPOTENCY_KEY);

        await supabase.auth.refreshSession();
      } catch (error) {
        if (cancelledRef.current) return;

        const errorMessage = error instanceof Error ? error.message : String(error);

        logError('frontend', 'onboarding_error', `Account creation failed: ${errorMessage}`, undefined, {
          url: window.location.href,
        });

        const isGithubTokenError = errorMessage.includes('GitHub Access Token');
        toastError(
          isGithubTokenError
            ? 'GitHub authentication expired. Please sign in again.'
            : 'Error while creating account. Please try signing in again.',
          true
        );

        await signOut();
      }
    };

    finalizeOnboarding();

    return () => {
      cancelledRef.current = true;
    };
  }, []);

  return (
    <VStack>
      <Text fontSize="xxxLarge">Creating your account</Text>
      <Text fontWeight="regular" opacity="secondary" pb={5}>
        We are making sure everything will be awesome.
      </Text>
      <Spinner size="lg" />
    </VStack>
  );
};

export default AccountCreatingStep;
